import { formatDeliveryFormat } from "../../lib/format.js";
import type {
  Decision,
  Delivery,
  Feed,
  FeedHealth,
  NowPlaying,
} from "../../api/types.js";

/**
 * What one row of the console says, worked out in one place and away from the screen
 * that draws it.
 *
 * Every feed answers the same six questions — is it switched on, does it publish
 * anything, what did the product establish about it, what is on it, what is next on
 * it, and what does it publish — and the answers are combined here so that a feed
 * with nothing to say and a feed in trouble are two values of one shape rather than
 * two branches in a component. Nothing in this file knows how many feeds there are.
 */

export type FeedTone = "ok" | "warn" | "err" | "idle";

export interface FeedStatus {
  tone: FeedTone;
  label: string;
  detail: string;
}

export interface DeliverySummary {
  count: number;
  primary: Delivery | null;
  /** How a listener would say it: `MP3 · 192 kbps · stereo`, or what is missing. */
  label: string;
}

export interface FeedConsoleRow {
  feed: Feed;
  live: NowPlaying | null;
  /** True only before this feed's first answer: never once anything is known. */
  liveLoading: boolean;
  liveError: string | null;
  health: FeedHealth | null;
  next: Decision | null;
  status: FeedStatus;
  deliveries: DeliverySummary;
  /** Whether this feed is asking the operator for something. */
  attention: boolean;
}

export interface FeedConsoleInput {
  feed: Feed;
  live?: NowPlaying | null;
  liveLoading?: boolean;
  liveError?: string | null;
  health?: FeedHealth | null;
  next?: Decision | null;
}

const HEALTH_STATUS = {
  healthy: { tone: "ok" as const, label: "On air" },
  degraded: { tone: "warn" as const, label: "Fallback" },
  down: { tone: "err" as const, label: "Off air" },
};

const PUBLISHED_STATUS = {
  healthy: { tone: "ok" as const, label: "On air" },
  degraded: { tone: "warn" as const, label: "Fallback" },
  down: { tone: "err" as const, label: "Off air" },
};

function describeDeliveries(live: NowPlaying | null): DeliverySummary {
  // Nothing has answered yet, so nothing is said. "Publishes nothing" is a claim,
  // and a claim made before the first read of a feed just created would be false.
  if (!live) return { count: 0, primary: null, label: "" };
  const deliveries = live.deliveries;
  const primary = deliveries[0] ?? null;
  if (!primary) {
    return { count: 0, primary: null, label: "Publishes nothing" };
  }
  const format = formatDeliveryFormat(primary);
  return {
    count: deliveries.length,
    primary,
    label:
      deliveries.length > 1
        ? `${format} · +${String(deliveries.length - 1)} more`
        : format,
  };
}

/**
 * One line for the row, in the order the operator would ask.
 *
 * A switched-off feed is not a failure and is never coloured as one — it is the
 * operator's own decision, said back to him plainly. A feed that publishes nothing
 * is not a failure either. Only what the product established by listening to the
 * feed can spend any of the surface's attention, and even then it says what it
 * established rather than raising an alarm about it.
 */
export function describeStatus(
  feed: Feed,
  live: NowPlaying | null,
  health: FeedHealth | null,
): FeedStatus {
  if (!feed.enabled) {
    return {
      tone: "idle",
      label: "Disabled",
      detail: "This feed is turned off.",
    };
  }
  if (!live && !health) {
    return {
      tone: "idle",
      label: "Not established",
      detail: "No status yet.",
    };
  }
  const deliveries = live?.deliveries ?? [];
  if (health ? !health.publishes : deliveries.length === 0) {
    return {
      tone: "idle",
      label: "No delivery",
      detail: "Add a delivery so listeners can tune in.",
    };
  }
  if (health && health.state !== "unknown") {
    const { tone, label } = HEALTH_STATUS[health.state];
    return {
      tone,
      label,
      detail: health.detail || firstConditionDetail(health) || label,
    };
  }
  if (live?.state) {
    const { tone, label } = PUBLISHED_STATUS[live.state];
    return { tone, label, detail: label };
  }
  return {
    tone: "idle",
    label: "Not established",
    detail: "No status yet.",
  };
}

function firstConditionDetail(health: FeedHealth): string {
  const condition = health.conditions[0];
  return condition ? condition.detail : "";
}

export function buildConsoleRow(input: FeedConsoleInput): FeedConsoleRow {
  const live = input.live ?? null;
  const health = input.health ?? null;
  const status = describeStatus(input.feed, live, health);
  return {
    feed: input.feed,
    live,
    liveLoading: (input.liveLoading ?? false) && live === null,
    liveError: input.liveError ?? null,
    health,
    next: input.next ?? null,
    status,
    deliveries: describeDeliveries(live),
    attention: status.tone === "warn" || status.tone === "err",
  };
}

export function buildConsoleRows(
  inputs: readonly FeedConsoleInput[],
): FeedConsoleRow[] {
  return inputs.map(buildConsoleRow);
}

export interface ConsoleSummary {
  total: number;
  onAir: number;
  attention: number;
  disabled: number;
}

/**
 * The one-line answer to "how are my feeds" — counts, never a single verdict, because
 * N feeds do not have one state between them and never will.
 */
export function summariseConsole(
  rows: readonly FeedConsoleRow[],
): ConsoleSummary {
  return {
    total: rows.length,
    onAir: rows.filter((row) => row.status.tone === "ok").length,
    attention: rows.filter((row) => row.attention).length,
    disabled: rows.filter((row) => !row.feed.enabled).length,
  };
}

/**
 * Feeds asking for something come first, then the rest in the order the API gave
 * them — which is by name. Nothing is ever hidden by this and no feed is singled out
 * as the current one: it is the same list, read in the order a person would want it.
 */
export function orderConsoleRows(
  rows: readonly FeedConsoleRow[],
): FeedConsoleRow[] {
  return [...rows].sort((left, right) => {
    if (left.attention !== right.attention) return left.attention ? -1 : 1;
    return 0;
  });
}
